/**
 * Campaign Domain Model
 * 
 * Encapsulates business logic and validation rules for campaigns.
 * Pure business logic - no Firestore operations.
 */

export class Campaign {
  constructor(data = {}) {
    // Core identification
    this.id = data.id || null;
    this.name = data.name || null;
    this.description = data.description || null;
    
    // Mailshot relationships
    this.mailshotIds = data.mailshotIds || [];
    
    // Statistics (cached)
    this.stats = data.stats || {
      totalMailshots: 0,
      totalProperties: 0,
      totalSent: 0,
      totalDelivered: 0,
    };
    
    // Status
    this.status = data.status || 'active'; // active, completed, archived
    
    // Timestamps
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.startedAt = data.startedAt || null;
    this.completedAt = data.completedAt || null;
  }

  /**
   * Calculate statistics from a list of mailshots
   * @param {Mailshot[]} mailshots - Array of Mailshot instances
   * @returns {Object} - Statistics object
   */
  calculateStats(mailshots) {
    const stats = {
      totalMailshots: mailshots.length,
      totalProperties: 0,
      totalSent: 0,
      totalDelivered: 0,
      byStatus: {
        draft: 0,
        sent: 0,
        completed: 0,
      },
    };
    
    mailshots.forEach(mailshot => {
      // Count properties (using propertyIds length)
      if (mailshot.propertyIds) {
        stats.totalProperties += mailshot.propertyIds.length;
      }
      
      // Count sent/delivered
      if (mailshot.sentCount) {
        stats.totalSent += mailshot.sentCount;
      }
      if (mailshot.deliveredCount) {
        stats.totalDelivered += mailshot.deliveredCount;
      }
      
      // Count by status
      if (mailshot.status && stats.byStatus[mailshot.status] !== undefined) {
        stats.byStatus[mailshot.status]++;
      }
    });
    
    return stats;
  }

  /**
   * Check if a mailshot is in this campaign
   * @param {string} mailshotId - Mailshot ID to check
   * @returns {boolean} - True if mailshot is in this campaign
   */
  hasMailshot(mailshotId) {
    return this.mailshotIds && this.mailshotIds.includes(mailshotId);
  }

  /**
   * Check if campaign can be deleted
   * @returns {boolean} - True if campaign can be deleted
   */
  canBeDeleted() {
    // Cannot delete if it has mailshots
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Get reason why campaign cannot be deleted
   * @returns {string|null} - Reason or null if can be deleted
   */
  getDeleteBlockReason() {
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      return `Campaign contains ${this.mailshotIds.length} mailshot(s)`;
    }
    
    return null;
  }

  /**
   * Get list of collections this campaign affects
   * Used for cascade operations
   * @returns {string[]} - Array of collection names
   */
  getAffectedCollections() {
    const affected = ['discovery_campaigns'];
    
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      affected.push('discovery_mailshots');
    }
    
    return affected;
  }

  /**
   * Check if campaign is active
   * @returns {boolean} - True if campaign is active
   */
  isActive() {
    return this.status === 'active';
  }

  /**
   * Check if campaign is completed
   * @returns {boolean} - True if campaign is completed
   */
  isCompleted() {
    return this.status === 'completed';
  }

  /**
   * Check if campaign is archived
   * @returns {boolean} - True if campaign is archived
   */
  isArchived() {
    return this.status === 'archived';
  }

  /**
   * Validate campaign data
   * @throws {Error} - If validation fails
   * @returns {boolean} - True if valid
   */
  validate() {
    if (!this.name || this.name.trim() === '') {
      throw new Error('name is required');
    }
    
    if (this.name.length > 100) {
      throw new Error('name must be 100 characters or less');
    }
    
    // Validate mailshotIds is array
    if (this.mailshotIds && !Array.isArray(this.mailshotIds)) {
      throw new Error('mailshotIds must be an array');
    }
    
    // Validate status
    const validStatuses = ['active', 'completed', 'archived'];
    if (this.status && !validStatuses.includes(this.status)) {
      throw new Error(`Invalid status: ${this.status}. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    return true;
  }

  /**
   * Serialize to Firestore document
   * @returns {Object} - Firestore document data
   */
  toFirestore() {
    const doc = {
      name: this.name,
      mailshotIds: this.mailshotIds,
      stats: this.stats,
      status: this.status,
    };
    
    // Optional fields
    if (this.description) doc.description = this.description;
    if (this.createdAt) doc.createdAt = this.createdAt;
    if (this.updatedAt) doc.updatedAt = this.updatedAt;
    if (this.startedAt) doc.startedAt = this.startedAt;
    if (this.completedAt) doc.completedAt = this.completedAt;
    
    return doc;
  }

  /**
   * Deserialize from Firestore document
   * @param {Object} doc - Firestore document snapshot
   * @returns {Campaign} - Campaign instance
   */
  static fromFirestore(doc) {
    if (!doc) return null;
    
    const data = doc.data ? doc.data() : doc;
    const id = doc.id || data.id;
    
    return new Campaign({
      id,
      ...data
    });
  }

  /**
   * Create a plain object for display (removes internal fields)
   * @returns {Object} - Plain object suitable for JSON serialization
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      mailshotCount: this.mailshotIds?.length || 0,
      stats: this.stats,
      status: this.status,
      createdAt: this.createdAt,
    };
  }
}
